import type { Theme } from '@mui/material/styles';
import { createContext, useContext } from 'react';

export const MAX_SCORE = 10;

export type ScoreBand = 'high' | 'medium' | 'low';

export interface ScoreThresholds {
  strongFrom: number;
  weakBelow: number;
}

// Bands come from the service response; a page provides them around everything it scores.
export const ScoreThresholdsContext = createContext<ScoreThresholds | null>(null);

export function useScoreThresholds(): ScoreThresholds {
  const thresholds = useContext(ScoreThresholdsContext);
  if (thresholds === null) throw new Error('ScoreThresholdsContext is not provided');
  return thresholds;
}

export function scoreBand(score: number, { strongFrom, weakBelow }: ScoreThresholds): ScoreBand {
  if (score >= strongFrom) return 'high';
  return score >= weakBelow ? 'medium' : 'low';
}

export function scoreText(theme: Theme, score: number, thresholds: ScoreThresholds): string {
  return theme.palette.score[scoreBand(score, thresholds)].text;
}

export function scoreTint(theme: Theme, score: number, thresholds: ScoreThresholds): string {
  return theme.palette.score[scoreBand(score, thresholds)].bg;
}

export function formatScore(score: number): string {
  return score.toFixed(1);
}

export function needsAttention(score: number, { weakBelow }: ScoreThresholds): boolean {
  return score < weakBelow;
}

export function formatRanAt(iso: string): string {
  const date = new Date(iso);
  const day = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(
    date,
  );
  return `${day}, ${time}`;
}

export function formatDayMonth(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(iso));
}
